// features/intelligence/tabs/briefing.js
// ═══════════════════════════════════════════════════════════
// BRIEFING — the prepared daily synthesis.
//
// Generated by Claude at 7:30am ET on trading days using the
// live data the dashboard reads (regime, VIX, yield curve,
// Fed news, today's events). One row per (domain, date).
//
// On mount, fetch /api/intelligence/briefing. If a row exists
// for today (or any recent day), render it. Otherwise fall
// back to the sample copy below so the tab is never blank.
//
// Display matches the original sample shape:
//   - 1-line eyebrow ("Today's Briefing — Apr 18, 2026")
//   - 2-sentence synthesis
//   - 3-5 numbered bullets with tone-colored left border
//   - Footer line ("Synthesized from N signals")
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';

// Tone → border color mapping (matches the brand palette)
const TONE_COLOR = {
  sage:   '#7a9e82',
  gold:   '#c8a96e',
  terra:  '#c46050',
  purple: '#9b8fc4',
};

// Fallback shown only when no briefing row exists yet (cold
// start, brand new install). Once Claude has generated even
// one briefing, that one is shown until the next 7:30am.
const SAMPLE = {
  synthesis: 'FOMC minutes at 2pm dominate the day. The market will trade cautiously into 2pm and decisively after — your edge is in the second half, not the first.',
  bullets: [
    { tone: 'sage',   bullet: 'Reduce sizes 20-30% before 2pm. Resume normal sizing post-release once direction confirms.', ctx: 'Trending elevated vol regime + an event = violent counter-trend moves within the trend. Position size, not entry selection, is today\'s most important decision.' },
    { tone: 'gold',   bullet: 'Watch NVDA $895 carefully. Heavy call buying at $900 next Friday creates mechanical hedging demand near $895.', ctx: 'Volume 3.4× open interest = new institutional positioning. Market makers will buy stock to delta-hedge as price approaches strike — price can accelerate without new buyers.' },
    { tone: 'purple', bullet: 'Squeeze setup is favored today: VIX > 18 + post-FOMC = 3 of 3 historical wins for you.', ctx: 'Your Signal Matrix flagged this pattern at 6:00 AM. Run the setup with normal rules — your edge condition is present.' },
    { tone: 'terra',  bullet: 'Avoid premium selling all day. Backwardation in VIX term structure means you\'d be selling into the event risk.', ctx: 'VIX9D > VIX spot = market pricing a near-term IV spike. Sellers pay full freight after the spike resolves.' },
  ],
  sourceCount: 12,
  generatedAt: null,
  briefingDate: null,
  isSample: true,
};

export default function BriefingTab() {
  const [data, setData] = useState(SAMPLE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/intelligence/briefing?domain=trading', { cache: 'no-store' });
        if (!res.ok) return;
        const payload = await res.json();
        if (cancelled) return;
        if (payload?.briefing?.synthesis) {
          setData({
            synthesis: payload.briefing.synthesis,
            bullets: payload.briefing.bullets || [],
            sourceCount: payload.briefing.sourceCount,
            generatedAt: payload.briefing.generatedAt,
            briefingDate: payload.briefing.briefingDate,
            isSample: false,
          });
        }
      } catch (err) {
        console.warn('[briefing] fetch failed:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="di-scroll">
      <div className="di-section" style={{ borderTop: '2px solid #c8a96e' }}>
        <div className="di-sec-header">
          <div className="di-sec-label">{formatHeaderDate(data.briefingDate)}</div>
          <div className="di-sec-right">
            <div className="di-sec-time">
              {data.isSample
                ? 'Sample briefing'
                : `Generated ${formatLocalTime(data.generatedAt)}`}
            </div>
          </div>
        </div>

        <div className="di-sec-body" style={{ padding: '20px 24px 24px' }}>
          {/* 2-sentence synthesis — Cormorant for the editorial weight */}
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 22, lineHeight: 1.6, color: '#f0eefa',
            marginBottom: 22,
          }}>
            {data.synthesis}
          </div>

          <div className="di-sec-lbl" style={{ marginBottom: 14 }}>What to do today</div>

          {data.bullets.map((item, i) => (
            <div
              key={i}
              style={{
                marginBottom: 14,
                paddingLeft: 24,
                borderLeft: `2px solid ${TONE_COLOR[item.tone] || TONE_COLOR.gold}`,
              }}
            >
              <div style={{
                fontSize: 14,
                color: '#f0eefa',
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 400,
                lineHeight: 1.6,
                marginBottom: item.ctx ? 6 : 0,
              }}>
                {item.bullet}
              </div>
              {item.ctx && (
                <div style={{
                  fontSize: 12,
                  color: '#888',
                  lineHeight: 1.6,
                  fontStyle: 'italic',
                }}>
                  {item.ctx}
                </div>
              )}
            </div>
          ))}

          <div style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #1e1e2a',
            fontFamily: 'DM Mono, monospace',
            fontSize: 9,
            color: '#555',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            {data.isSample
              ? 'Sample data · Live briefing arrives 7:30 AM ET'
              : `Synthesized from ${data.sourceCount || 0} signals · Domain Intelligence shared layer`}
          </div>
        </div>
      </div>

      {loading && !data.briefingDate && (
        <div style={{
          textAlign: 'center',
          padding: 16,
          color: '#555',
          fontSize: 11,
          fontFamily: 'DM Mono, monospace',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}>
          Loading briefing…
        </div>
      )}
    </div>
  );
}

function formatHeaderDate(ymd) {
  const date = ymd ? new Date(ymd + 'T12:00:00') : new Date();
  const m = date.toLocaleDateString('en-US', { month: 'short' });
  const d = date.getDate();
  const y = date.getFullYear();
  return `Today's Briefing — ${m} ${d}, ${y}`;
}

function formatLocalTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch (_e) {
    return '';
  }
}
